<script setup lang="ts">
import { computed, ref } from 'vue';
import Loading from '@/components/ui/Loading.vue';
import axios from 'axios';

const props = defineProps({
  uploadFile: {
    type: Function,
    required: true,
  },
  previousFile: {
    type: File,
    required: false,
    default: undefined,
  },
});

enum errorMessageMap {
  'FILE_TYPE_INVALID' = ': Invalid file type.',
  'FILE_TOO_LARGE' = ': File must be smaller than 10mb.',
  'DEFAULT' = ': Please try again.',
}

const isDragOver = ref(false);
const isUploading = ref(false);
const error = ref<string | null>(null);
const uploadedFile = ref<File | null>(null);
const currentFile = computed(() => uploadedFile.value ?? props.previousFile);

async function handleOnChange(event: Event) {
  const input = event.target as HTMLInputElement;
  const files = input.files;
  if (files && files.length > 0) {
    const file = files[0];
    if (file) {
      await handleFileUpload(file);
    }
  }
}

async function handleOnDrop(event: DragEvent) {
  isDragOver.value = false;

  const files = event.dataTransfer?.files;
  if (files && files.length > 0) {
    const file = files[0];
    if (file) {
      await handleFileUpload(file);
    }
  }
}

function createErrorMessageFromCode(errorCode: string): string {
  const errorMessage = errorMessageMap[errorCode] ?? errorMessageMap['DEFAULT'];
  return `File upload failed${errorMessage}`;
}

defineExpose({
  createErrorMessageFromCode,
});

async function handleFileUpload(file: File) {
  isUploading.value = true;
  error.value = null;
  uploadedFile.value = null;
  try {
    isUploading.value = true;
    await props.uploadFile(file);
    uploadedFile.value = file;
  } catch (err) {
    if (axios.isAxiosError(err)) {
      error.value = createErrorMessageFromCode(err.response?.data.errorCode);
    } else {
      error.value = `File upload failed${errorMessageMap['DEFAULT']}`;
    }
  } finally {
    isUploading.value = false;
    if (uploadedFile.value === file) {
      error.value = null;
    }
  }
}

function inputClick() {
  document.querySelector('input')?.click();
}
</script>

<template>
  <div>
    <input
      type="file"
      accept="application/pdf,image/jpeg,image/png,image/gif,image/webp,image/tiff,image/bmp,image/heif,image/heic,image/avif"
      class="hidden"
      @change="handleOnChange"
    />
    <div
      id="dropArea"
      class="border-light-brd-drop-area bg-light-drop-area flex h-[140px] w-full flex-col items-center justify-center rounded-lg border-[1px] border-dashed"
      :class="{
        'border-light-brd-drop-area-active': isDragOver,
        'bg-light-drop-area-active': isDragOver,
      }"
      @dragover.prevent="isDragOver = true"
      @dragleave.prevent="isDragOver = false"
      @drop.prevent="handleOnDrop"
    >
      <div class="flex flex-col items-center justify-center" v-if="!isUploading">
        <div v-if="!currentFile">
          <ArrowUpOnSquareIcon class="w-5" />
        </div>
        <div class="flex flex-col items-center justify-center" v-else>
          <DocumentTextIcon class="w-5" />
          <div class="text-light-file-name mb-2">{{ currentFile.name }}</div>
        </div>
        <button class="font-bold underline" @click="inputClick" data-test="file-upload">
          Upload a .pdf or image file less than 10MB
        </button>
      </div>
      <div class="flex flex-col items-center justify-center" v-else>
        <Loading class="h-10 w-10" v-if="isUploading" />
      </div>
      <div v-if="error" class="text-dark-utility-red mt-2 text-center text-xs">
        {{ error }}
      </div>
    </div>
  </div>
</template>
